import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

interface CustomThemeOptions {
  custom?: {
    green: string;
    greenLight: string;
    yellow: string;
    moodBarPurple: string;
    purpleDark: string;
    purpleLight: string;
    fancyBlack: string;
    inboxRed: string;
    grey: string;
    red: string;
    cyan: string;
    darkBlue: string;
    gold: string;
    orange: string;
    almostBlack: string;
    lightYellow: string;
    systemBetTypeCardBackgroundColor: string;
    systemBetTypeCardBorderColor: string;
    azure: string;
    systemBetBestKwiffDivider: string;
  };
  featuredPage?: {
    backgroundColor: string;
    cardGradientBackground: string;
    sportIconBackground: string;
    greenBorder: string;
    goldenBorder: string;
    divider: string;
    trendingIconColor: string;
  };
  login?: {
    loginOptionBackground: string;
  };
}

export const SCREEN_SIZE = { XXL: 1440, XXXL: 1920 };

export const FEATURED_PAGE_GREEN = '#4EC844';
export const FEATURED_PAGE_GOLD = '#D4A865';

export const CASINO_GAME_TAG_AMBER = '#D6AB5A';
export const CASINO_GAME_TAG_DARK_BLUE = '#1C04AC';
export const CASINO_GAME_TAG_ORANGE = '#F58023';
export const CASINO_GAME_TAG_RED = '#CD1A02';
export const CASINO_GAME_TAG_GREEN = '#02CD03';
export const CASINO_GAME_TAG_PURPLE = '#7B16F3';

interface KwiffThemeOptions extends ThemeOptions, CustomThemeOptions {}
export interface KwiffTheme extends Theme, CustomThemeOptions {}

export const SMALL_SCREEN_INTERMEDIATE_SIZE = 400;
export const BIG_SCREEN_INTERMEDIATE_SIZE = 1440;
export const RUNNER_TOOLTIP_375_SCREEN_WIDTH_BOUND = 375;
export const RUNNER_TOOLTIP_400_SCREEN_WIDTH_BOUND = 400;

export const SCREEN_WIDTH_IPHONE8_PLUS = 415;
export const SCREEN_WIDTH_280 = 280;
export const SCREEN_WIDTH_300 = 300;
export const SCREEN_WIDTH_330 = 330;
export const SCREEN_WIDTH_340 = 340;
export const SCREEN_WIDTH_350 = 350;
export const SCREEN_WIDTH_360 = 360;
export const SCREEN_WIDTH_385 = 385;
export const SCREEN_WIDTH_400 = 400;
export const SCREEN_WIDTH_420 = 420;
export const SCREEN_WIDTH_430 = 430;
export const SCREEN_WIDTH_450 = 450;
export const SCREEN_WIDTH_500 = 500;
export const SCREEN_WIDTH_590 = 590;
export const SCREEN_WIDTH_635 = 635;
export const SCREEN_WIDTH_765 = 765;
export const SCREEN_WIDTH_820 = 820;
export const SCREEN_WIDTH_935 = 935;
export const SCREEN_WIDTH_1024 = 1024;
export const SCREEN_WIDTH_1100 = 1100;
export const SCREEN_WIDTH_1250 = 1250;
export const SCREEN_WIDTH_1400 = 1400;

export const SCREEN_SIZE_700 = 700;

export const kwiffTheme = {
  palette: {
    primary: {
      light: '#9249FF',
      main: '#8225FA',
      dark: '#5D00CD',
    },
    secondary: {
      main: '#C49A4D',
      dark: '#809593',
    },
    grey: {
      900: '#142323',
      800: '#798c8c', // dark grey
      600: '#757575', // a bit more grey
      500: '#8c8c8c', // medium grey
      400: '#bfbfbf', // some grey
      300: '#cccccc', // grey
      200: '#f2f2f2', // light grey
      100: '#fafafa', // very light grey
    },
  },
  common: {
    white: '#FFFFFF',
    black: '#000000',
  },
  featuredPage: {
    backgroundColor: '#121212',
    cardGradientBackground:
      'linear-gradient(106.51deg, rgba(255, 255, 255, 0.25) -30.62%, rgba(255, 255, 255, 0.25) -30.62%, rgba(255, 255, 255, 0.25) -30.61%, rgba(255, 255, 255, 0.175) 30.64%, rgba(255, 255, 255, 0.172527) 30.66%, rgba(255, 255, 255, 0.075) 100%)',
    greenBorder: `2px solid ${FEATURED_PAGE_GREEN}`,
    goldenBorder: `2px solid ${FEATURED_PAGE_GOLD}`,
    sportIconBackground: 'rgba(148, 148, 148, 0.2)',
    divider: '#4A4E4F',
    trendingIconColor: '#D4A86540',
  },
  login: {
    loginOptionBackground: '#F0E5FE',
  },
  custom: {
    purpleDark: '#42137E',
    purpleLight: '#7325D9',
    green: '#3ECE25',
    greenLight: '#00D300',
    yellow: '#FFFF00',
    moodBarPurple: '#3B1D77',
    fancyBlack: '#141a1a',
    inboxRed: '#C41E3A',
    grey: '#949494',
    red: '#FF0000',
    cyan: '#009CDE',
    darkBlue: '#544292',
    gold: '#E9B850',
    orange: '#F29C38',
    almostBlack: 'rgba(0, 0, 0, 0.87)',
    lightYellow: '#FFD600',
    systemBetTypeCardBackgroundColor: '#1A2729',
    systemBetTypeCardBorderColor: '#F1D69D',
    azure: '#F2F3F3',
    systemBetBestKwiffDivider: '#D2B67A',
  },
  typography: {
    // useNextVariants: true,
    fontFamily: 'visuelt',
    fontSize: 14,
    fontDisplay: 'swap',
  },
  breakpoints: {
    values: {
      xs: SCREEN_WIDTH_330,
      sm: 700,
      md: 960,
      lg: SCREEN_WIDTH_1024,
      xl: 1920,
    },
  },
  components: {
    MuiStepLabel: {
      styleOverrides: {
        iconContainer: {
          fontSize: '12px',
        },
        labelContainer: {
          fontSize: '6px',
          marginTop: '0px',
          padding: '0px',
        },
        alternativeLabel: {
          marginTop: '0px',
        },
        label: {
          marginTop: '0px',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        body2: {
          marginTop: '0px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '2px 6px 0px 0px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: '#F2F2F2',
          justifyContent: 'center',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          display: 'block',
        },
      },
    },
  },
};

export const CUSTOM_COLORS = {
  DARK_GRADIENT:
    'linear-gradient(106.51deg, rgba(255, 255, 255, 0.25) -30.62%, rgba(255, 255, 255, 0.25) -30.62%, rgba(255, 255, 255, 0.25) -30.61%, rgba(255, 255, 255, 0.175) 30.64%, rgba(255, 255, 255, 0.172527) 30.66%, rgba(255, 255, 255, 0.075) 100%)',
  DIVIDER: '#4A4E4F',
  SPORT_ICON_BACKGROUND: 'rgba(148, 148, 148, 0.2)',
  GREEN: '#3ECE25',
  GOLD: '#E9B850',
  PROJECT_PHOENIX_LIGHT: '#FFC53A',
  PROJECT_PHOENIX_MAIN: '#FF5A00',
  PROJECT_PHOENIX_DARK: '#A13900',
  PROJECT_PHOENIX_TOP_BAR: '#222222',
  PROJECT_PHOENIX_SECONDARY_MAIN: '#964B00',
  PROJECT_PHOENIX_SECONDARY_DARK: '#C4A484',
  DARK_ORANGE: '#A54F20',
  DARK_BLUE: '#544292',
  BLUE_PURPLE: '#6850B3',
  CLOSE_TO_GOLD: '#D4B779',
  DARK_SHADE_CYAN: '#3A4649',
  CASHOUT_ACCEPTED_COLOR_1: '#2B2A5A',
  CASHOUT_ACCEPTED_COLOR_2: '#402A81',
  CASHOUT_ACCEPTED_COLOR_3: '#4F24A4',
  CASHOUT_ACCEPTED_COLOR_4: '#5E1CC1',
  GREY: '#919191',
  NAVIGATION_BACKGROUND_COLOR: '#CCCCFF50',
  NAVIGATION_HOVER_BACKGROUND_COLOR: '#c092fc10',
};

export const DEFAULT_DIVIDER_HEIGHT = 5;

export const ACHIEVEMENT_CARD_SIZE = {
  HEIGHT: 200,
  WIDTH: 190,
};

export const SPORT_ICON_ON_CARD_BORDER_RADIUS = 8;

export const mobileMaxResolution = kwiffTheme.breakpoints!.values!.lg!;
export const CASINO_TILE_SMALL_MOBILE_WIDTH = 380;
export const CASINO_FILTER_TILE_HALF_SIZE_BELOW_WIDTH = 500;
export const CASINO_TILE_XXL_WIDTH_BOUND = 2100;
export const CASINO_TILE_XL_WIDTH_BOUND = 1720;
export const CASINO_TILE_BIG_WIDTH_LOWER_BOUND = 1440;
export const CASINO_TILE_INTERMEDIATE_BOUND = 1300;
export const CASINO_TILE_SMALL_WIDTH_UPPER_BOUND = 1024;
export const CASINO_PROMO_BAR_BIG_WIDTH = 1200;
export const CASINO_ONE_TILE = 1;
export const CASINO_TWO_TILES = 2;
export const CASINO_THREE_TILES = 3;
export const CASINO_FOUR_TILES = 4;
export const CASINO_FIVE_TILES = 5;
export const CASINO_SIX_TILES = 6;
export const CASINO_TEN_TILES = 10;
export const CASINO_TILE_SM_TL_WIDTH = 131;
export const CASINO_TILE_RIGHT_MARGIN = 8;
export const CASINO_TILE_LIST_LEFT_MARGIN = 8;

export const TOP_BAR_HEIGHT = 56;
export const BOTTOM_NAV_BAR_HEIGHT = 56;
export const HEADER_BUTTON_HEIGHT = 48;
export const INSTANT_GAMES_HEADER_HEIGHT = 50;
export const PROFILE_HEADER_HEIGHT = 48;
export const SPORTS_NAV_TAB_HEIGHT = 62;
export const CLOSE_BAR_HEIGHT = 32;
export const STREAM_BUTTON_HEIGHT = 32;
export const SMALL_STREAM_BUTTON_HEIGHT = 20;
export const TRENDING_CARD_TEAMS_ROW_HEIGHT = 68;

export const MEETING_RACES_ROW_HEIGHT = 45;

export const APP_INSTALL_PROMPT_WIDTH = '96vw';
export const APP_INSTALL_PROMPT_HEIGHT = '24vh';
export const APP_INSTALL_PROMPT_HEIGHT_SMALL_DEVICE = '28vh';
export const APP_INSTALL_PROMPT_KWIFF_LOGO_SQUARE_SIZE = 48;

export const SIGNUP_OFFERS_AND_REWARDS_BUTTON_WIDTH = '48%';

export const FEATURED_TRENDING_BETS_BORDER_THICKNESS = 8;

export const CASINO_MOBILE_CONTAINER_SIDE_PADDING = 12;
export const CASINO_SQUARE_TILE_SIDE_PADDING = 6;
export const CASINO_SQUARE_TILE_DESKTOP_BORDER_THICKNESS = 8;
export const CASINO_SQUARE_TILE_MOBILE_BORDER_THICKNESS = 6;
export const CASINO_TAGGED_TILE_MOBILE_BORDER_THICKNESS = 2;
export const CASINO_TAGGED_TILE_DESKTOP_BORDER_THICKNESS = 3;

export const CASINO_TAGGED_MOBILE_ICON_HEIGHT = 20;
export const CASINO_TAGGED_DESKTOP_ICON_HEIGHT = 26;

export const CASINO_SQUARE_TILE_BORDER_TEXT_HEIGHT = 16;

export const CASINO_MOBILE_TOP_PADDING_FOR_ICONS = CASINO_TAGGED_MOBILE_ICON_HEIGHT / 2;
export const CASINO_DESKTOP_TOP_PADDING_FOR_ICONS = CASINO_TAGGED_DESKTOP_ICON_HEIGHT / 2;

export const CASINO_SQUARE_TILE_HORIZONTAL_PADDING = 2 * CASINO_SQUARE_TILE_SIDE_PADDING;
export const DESKTOP_CASINO_TILE_HORIZONTAL_PADDING_AND_TEXT_BORDER =
  CASINO_SQUARE_TILE_HORIZONTAL_PADDING + 2 * CASINO_SQUARE_TILE_DESKTOP_BORDER_THICKNESS;
export const DESKTOP_CASINO_TILE_HORIZONTAL_PADDING_AND_ICON_BORDER =
  CASINO_SQUARE_TILE_HORIZONTAL_PADDING + 2 * CASINO_TAGGED_TILE_DESKTOP_BORDER_THICKNESS;

export const MOBILE_CASINO_TILE_BORDER_AND_BORDER_TEXT_HEIGHT =
  CASINO_SQUARE_TILE_BORDER_TEXT_HEIGHT + 2 * CASINO_SQUARE_TILE_MOBILE_BORDER_THICKNESS;
export const DESKTOP_CASINO_TILE_BORDER_AND_BORDER_TEXT_HEIGHT =
  CASINO_SQUARE_TILE_BORDER_TEXT_HEIGHT + 2 * CASINO_SQUARE_TILE_DESKTOP_BORDER_THICKNESS;

export const DESKTOP_CASINO_TILE_BORDER_AND_ICON_HEIGHT =
  CASINO_DESKTOP_TOP_PADDING_FOR_ICONS + 2 * CASINO_TAGGED_TILE_DESKTOP_BORDER_THICKNESS;

export const NUMBER_OF_FULL_CASINO_TILES_IN_VIEW_ON_MOBILE = 3;
export const NUMBER_OF_FULL_CASINO_TILES_IN_VIEW_ON_NARROW_TABLET = 4;
export const NUMBER_OF_FULL_CASINO_TILES_IN_VIEW_ON_WIDE_TABLET = 5;
export const VISIBLE_WIDTH_OF_LAST_CASINO_TILE_ON_MOBILE = CASINO_SQUARE_TILE_SIDE_PADDING + 16;

export const CASINO_SQUARE_TILE_SIZE_170 = 170;
export const CASINO_SQUARE_TILE_SIZE_150 = 150;
export const CASINO_SQUARE_TILE_SIZE_90 = 90;

export const FEATURED_HORSE_RACING_RUNNING_ANIMATION_HEIGHT = 150;
export const FEATURED_HORSE_RACING_RUNNING_ANIMATION_WIDTH = 200;

export const SUPERCHARGED_BETS_CAROUSEL_HEIGHT = 80;

export const SIGNUP_DESKTOP_BANNER_SQUARE_MIN_WINDOW_HEIGHT = 1030;
export const SIGNUP_MOBILE_BANNER_SQUARE_MIN_WINDOW_HEIGHT = 790;

export const ADDRESS_PICKER_DIALOG_TITLE_HEIGHT = 64;
export const ADDRESS_PICKER_DIALOG_ACTIONS_HEIGHT = 47;
export const ADDRESS_PICKER_DIALOG_CONTENT_PADDINGS_HEIGHT = 16;

export const SIDE_NAV_BAR_EXPANDED_WIDTH = 225;
export const SUPERCHARGED_BETS_TICKER_CARD_HEIGHT = 150;

export const DROPDOWN_PAYMENT_LOGO_MAX_WIDTH = 40;

export const MOODBAR_HEIGHT = 38;
export const DEFAULT_BORDER_RADIUS = 10;
export const BORDER_RADIUS_5 = 5;

export const MULTIPLE_CARD_HEIGHT = 48;

export const SYSTEM_BET_BOOST_ENVELOPE_SQUARE_DESKTOP = 160;
export const SYSTEM_BET_BOOST_ENVELOPE_SQUARE_MOBILE = 130;

export const BOTTOM_INBOX_PADDING = 84;
export const INBOX_HEADER_BAR_HEIGHT = 40;
export const INBOX_CONTENT_HEIGHT = 500;
export const MOBILE_INBOX_ALERT_IMAGE_SIZE = '20vw';
export const DESKTOP_INBOX_ALERT_IMAGE_SIZE = 48;

export const PROFILE_BUTTON_HEIGHT = 36;

export const BALANCE_DISPLAY_ALTERNATE_MS = 5000;

export const BALANCE_BUTTON_WIDTH = 80;

export const SYSTEM_BET_TICKET_STATUS_SQUARE_SIZE = 16;

export const POST_HOG_SURVEY_PROMPT_MAX_WIDTH = 480;

export const MY_PROMOTION_NAME_HEIGHT = 22;

export const MY_PROMOTION_CARD_WIDTH_ASPECT_RATION = 1.25;

export const MY_PROMOTIONS_ELEMENT_OVERLAY_ZINDEX = 2;
export const MY_PROMOTIONS_CONFIRMATION_ELEMENT_ZINDEX = 4;

export const MY_PROMOTIONS_CARD_BUTTON_HEIGHT = 36;

export const PROFIT_AND_LOSS_BAR_HEIGHT = 56;

export const PROJECT_PHOENIX_PALETTE = {
  palette: {
    primary: {
      light: CUSTOM_COLORS.PROJECT_PHOENIX_LIGHT,
      main: CUSTOM_COLORS.PROJECT_PHOENIX_MAIN,
      dark: CUSTOM_COLORS.PROJECT_PHOENIX_DARK,
    },
    secondary: {
      main: CUSTOM_COLORS.PROJECT_PHOENIX_SECONDARY_MAIN,
      dark: CUSTOM_COLORS.PROJECT_PHOENIX_SECONDARY_DARK,
    },
  },
};

const createMuiTheme = (options?: KwiffThemeOptions): Theme => {
  let baseTheme = { ...(kwiffTheme as KwiffThemeOptions) };

  if (options && options.palette && options.palette.primary) {
    baseTheme = {
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        primary:
          baseTheme.palette && baseTheme.palette.primary
            ? {
                ...baseTheme.palette.primary,
                ...options.palette.primary,
              }
            : { ...options.palette.primary },
        secondary:
          baseTheme.palette && baseTheme.palette.secondary
            ? {
                ...baseTheme.palette.secondary,
                ...options.palette.secondary,
              }
            : { ...options.palette.secondary },
      },
    };
  }

  return createTheme(baseTheme);
};

export default createMuiTheme;
